import React from "react";
import { Link } from "react-router-dom";
import Gleap from "gleap";
import { PRE_EP_LINKS } from "../../config/preEpLinks";
import { PreEpChecklist } from "../../api/fetchPreEpChecklist";
import { PreEpExternalItem } from "../../api/updatePreEpChecklistItem";
import {
  ArrowIcon,
  CheckIcon,
  preEpButtonBase,
  preEpDisabledButton,
  preEpOutlineButton,
  preEpPrimaryButton,
} from "../PreEpUi/PreEpUi";

type StepState =
  /** Lives on an outside site: done once the member opens it from here. */
  | { kind: "external"; item: PreEpExternalItem; done: boolean }
  | { kind: "registration"; registered: boolean; available: boolean };

interface Step {
  title: string;
  tag?: string;
  body: string;
  action: string;
  /** Another action label once the step is done (e.g. register more people). */
  doneAction?: string;
  /** External URL (new tab) or in-app route. */
  href: string;
  startHere?: boolean;
  state: StepState;
}

const buildSteps = (checklist: PreEpChecklist | null): Step[] => [
  {
    title: "Register for Executive Perspective",
    body: "Executive Perspective (EP) is a foundational part of your CertainPath membership. You’ll dig into pricing, people, numbers and growth, and leave with a clear path forward. Register now, plan to be fully present, and bring the key managers who’ll help put the work into action.",
    action: "Register for EP",
    doneAction: "Register someone else",
    href: "/pre-ep/register/executive-perspective",
    startHere: true,
    state: {
      kind: "registration",
      registered: checklist?.executivePerspective.registered ?? false,
      available: (checklist?.executivePerspective.events.length ?? 0) > 0,
    },
  },
  {
    title: "Complete your Culture Index Profile",
    body: "This quick survey shows us how you lead and make decisions, so we can match you with the right CertainPath coaches. Answer honestly — the more real your answers, the better your match.",
    action: "Take the Culture Index",
    href: PRE_EP_LINKS.cultureIndex,
    state: {
      kind: "external",
      item: "culture-index",
      done: checklist?.cultureIndexDone ?? false,
    },
  },
  {
    title: "Register for a CertainPath Group Welcome Session",
    body: "Get oriented, learn how to get started and ask any initial questions, so you head into EP confident and with a CertainPath network already started!",
    action: "Register for your welcome session",
    doneAction: "Register someone else",
    href: "/pre-ep/register/welcome-session",
    state: {
      kind: "registration",
      registered: checklist?.welcomeSession.registered ?? false,
      available: (checklist?.welcomeSession.events.length ?? 0) > 0,
    },
  },
  {
    title: "Complete your CertainPath Activation Course",
    tag: "Recommended Before EP",
    body: "Learn the foundations every successful home service business is built on, so you arrive at EP ready to build on that successful foundation with the lessons you’ll learn from the CertainPath team.",
    action: "Start the course",
    href: PRE_EP_LINKS.activationCourse,
    state: {
      kind: "external",
      item: "activation-course",
      done: checklist?.activationCourseDone ?? false,
    },
  },
];

const isDone = (state: StepState): boolean =>
  state.kind === "registration" ? state.registered : state.done;

const StepAction: React.FC<{
  step: Step;
  done: boolean;
  onOpenExternal: (item: PreEpExternalItem) => void;
}> = ({ step, done, onOpenExternal }) => {
  const look =
    step.startHere && !done ? preEpPrimaryButton : preEpOutlineButton;
  const label = done && step.doneAction ? step.doneAction : step.action;

  if (step.state.kind === "registration" && !step.state.available) {
    return (
      <button
        className={`${preEpButtonBase} ${preEpDisabledButton}`}
        disabled
        title="Coming soon"
        type="button"
      >
        {step.action}
        <ArrowIcon />
      </button>
    );
  }
  if (step.href.startsWith("/")) {
    return (
      <Link className={`${preEpButtonBase} ${look}`} to={step.href}>
        {label}
        <ArrowIcon />
      </Link>
    );
  }
  return (
    <a
      className={`${preEpButtonBase} ${look}`}
      href={step.href}
      onClick={() => {
        if (step.state.kind === "external") onOpenExternal(step.state.item);
      }}
      rel="noopener noreferrer"
      target="_blank"
    >
      {label}
      <ArrowIcon />
    </a>
  );
};

interface PreEpStepsProps {
  firstName: string;
  checklist: PreEpChecklist | null;
  /** The member opened an outside-site step: mark it done. */
  onOpenExternal: (item: PreEpExternalItem) => void;
}

/**
 * The four getting-started steps for a Pre-EP member. The registration
 * steps tick themselves once someone at the company is registered; the
 * outside-site steps (Culture Index, Activation Course) are done as soon as
 * the member opens them from here.
 */
const PreEpSteps: React.FC<PreEpStepsProps> = ({
  firstName,
  checklist,
  onOpenExternal,
}) => {
  const steps = buildSteps(checklist);

  return (
    <div className="relative mx-auto flex max-w-[1000px] flex-col gap-10 px-5 pb-16 pt-8 md:px-11 md:pt-12">
      <section className="flex flex-col items-center gap-3.5 text-center">
        <span className="inline-flex min-h-[26px] items-center gap-2 rounded-full border border-[#2C3A66] bg-[#111D40] px-[11px] text-[13px] font-bold text-[#E6EAF5]">
          <span
            aria-hidden="true"
            className="h-[7px] w-[7px] rounded-full bg-[#E0103F]"
          />
          Pre-EP stage
        </span>
        <h1 className="m-0 text-[34px] font-light tracking-[0.01em] md:text-[52px]">
          Welcome to CertainPath, {firstName}
        </h1>
        <p className="m-0 max-w-[640px] text-lg leading-normal text-[#C3CBE0]">
          We are glad you are here! These steps get you ready for everything
          that&rsquo;s ahead with CertainPath, including your Executive
          Perspective week.
        </p>
      </section>

      <section className="flex flex-col gap-1">
        <h2 className="m-0 text-2xl font-bold">Let&rsquo;s get started.</h2>
        <p className="m-0 text-[15px] text-[#C3CBE0]">
          Here are your next steps.
        </p>
        <ol className="m-0 mt-5 flex list-none flex-col gap-3.5 p-0">
          {steps.map((step, i) => {
            const done = isDone(step.state);
            const highlighted = step.startHere && !done;
            return (
              <li
                className={`flex flex-wrap items-center gap-4 rounded-xl bg-[#131F45] p-5 sm:gap-[22px] md:p-7 ${highlighted ? "border border-[#E0103F]" : "border border-[#23305A]"}`}
                key={step.title}
              >
                <span
                  aria-hidden="true"
                  className={`flex h-11 w-11 flex-none items-center justify-center rounded-full text-lg font-extrabold ${done ? "bg-[#1F9D6B]" : highlighted ? "bg-[#E0103F]" : "border-2 border-[#3A4A7E]"}`}
                >
                  {done ? <CheckIcon /> : i + 1}
                </span>
                <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-1.5">
                  <h3 className="m-0 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[21px] font-bold">
                    {step.title}
                    {highlighted && (
                      <span className="rounded-full bg-[#E0103F] px-2.5 py-0.5 text-xs font-extrabold uppercase tracking-[0.04em]">
                        Start here
                      </span>
                    )}
                    {step.tag && !done && (
                      <span className="text-sm font-normal text-[#AEB8D3]">
                        {step.tag}
                      </span>
                    )}
                    {done && (
                      <span className="text-sm font-semibold text-[#5FD3A6]">
                        {step.state.kind === "registration"
                          ? "Registered"
                          : "Done"}
                      </span>
                    )}
                  </h3>
                  <p className="m-0 text-[15px] leading-[1.55] text-[#D3D9EA]">
                    {step.body}
                  </p>
                </div>
                <StepAction
                  done={done}
                  onOpenExternal={onOpenExternal}
                  step={step}
                />
              </li>
            );
          })}
        </ol>
      </section>

      <section className="flex flex-col items-start gap-4 rounded-xl border border-[#23305A] bg-[#131F45] p-5 sm:flex-row sm:items-center sm:gap-[18px] sm:px-7 sm:py-6">
        <span
          aria-hidden="true"
          className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#1B274B]"
        >
          <svg
            fill="none"
            height="20"
            stroke="#FF8AA3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="20"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="m-0 text-lg font-bold">
            Need help? Our coaches are here for you.
          </h2>
          <p className="m-0 text-[15px] text-[#C3CBE0]">
            Join a group follow-up and support session with the CertainPath team
            whenever you have questions or need a hand getting started.
          </p>
          {checklist?.supportSession.registered && (
            <p className="m-0 text-sm font-semibold text-[#5FD3A6]">
              You&rsquo;re registered for a support session.
            </p>
          )}
        </div>
        {(checklist?.supportSession.events.length ?? 0) > 0 ? (
          <Link
            className={`${preEpButtonBase} ${preEpOutlineButton}`}
            to="/pre-ep/register/support-session"
          >
            Register for a support session
            <ArrowIcon />
          </Link>
        ) : (
          // No support session is offered yet: the help widget instead.
          <button
            className={`${preEpButtonBase} ${preEpOutlineButton}`}
            onClick={() => Gleap.open()}
            type="button"
          >
            Get help
          </button>
        )}
      </section>
    </div>
  );
};

export default PreEpSteps;
